import apiClient from '../client';
import ENDPOINTS from '../endpoints';

/* The class checklist (roomParentTodos). A task either belongs to an event —
   eventId is the event's own `eventId`, not its Mongo _id — or has an empty one,
   which puts it in the General group. The list endpoint resolves both the group
   name and the assignees for us, so the UI doesn't have to join anything. */

const pad = (n) => String(n).padStart(2, '0');

/* dueDate comes back as a full ISO timestamp; the UI works in yyyy-mm-dd. */
const toISODate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

function normalizePerson(u) {
  return {
    id: u?.id || '',
    /* the API builds this from firstName + lastName, so it can come back blank */
    name: (u?.name || '').trim(),
    picture: u?.profilePic || null,
  };
}

/* Completion is stored per user. The list resolves it into three things: whether
   anyone has ticked the task off (`isCompleted` — the current server rule is
   that one assignee finishes it for everyone), whether *I* was one of them, and
   who the others are. */
function doneState(t) {
  return {
    done: typeof t?.isCompleted === 'boolean' ? t.isCompleted : t?.status === 'completed',
    doneByMe: t?.isCompletedByMe === true,
    doneCount: Number(t?.completedCount ?? (t?.completedBy || []).length) || 0,
    doneBy: (t?.completedBy || []).map((c) => normalizePerson(c?.user || {})).filter((p) => p.name),
  };
}

function normalizeTodo(t) {
  const owners = (t?.assignedUsers || []).map(normalizePerson);
  /* The task's own eventId decides its group — `group` only mirrors it after a
     lookup, so grouping on that would drop a task into General whenever the
     lookup or the field is missing. Empty eventId = General. */
  const eventId = t?.eventId || t?.group?.eventId || '';
  return {
    id: t?.todoId || t?._id || '',
    task: t?.task || '',
    due: toISODate(t?.dueDate),
    status: t?.status || 'pending',
    ...doneState(t),
    eventId,
    groupId: eventId,
    /* the event's name as the server resolved it; '' when it couldn't, and the
       page falls back to the event list it already has */
    groupName: t?.group?.name || '',
    owners,
    /* the raw ids, for sending back on an edit */
    assignedTo: Array.isArray(t?.assignedTo) ? t.assignedTo : owners.map((o) => o.id).filter(Boolean),
    createdBy: t?.createdBy || '',
  };
}

const todosService = {
  /* Returns { todos, total, totalPages }. Pass eventId: '' to narrow to General
     — omit it entirely for every group. */
  list: async (networkId, { page = 1, limit = 100, search = '', status, eventId, assignedTo } = {}) => {
    const res = await apiClient.get(ENDPOINTS.ROOM_PARENT_ADMIN.TODOS_BY_NETWORK(networkId), {
      params: {
        page,
        limit,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        ...(eventId !== undefined ? { eventId } : {}),
        ...(assignedTo ? { assignedTo } : {}),
      },
    });
    const data = res?.data ?? res;
    const raw = data?.todos || (Array.isArray(data) ? data : []);
    const total = Number(data?.totalCount ?? raw.length);
    return {
      todos: raw.map(normalizeTodo),
      total,
      totalPages: Number(data?.totalPages ?? Math.max(1, Math.ceil(total / limit))),
    };
  },

  /* assignedTo needs at least one user id — the API rejects an empty list. */
  create: async ({ networkId, task, assignedTo, dueDate, eventId, status }) => {
    const res = await apiClient.post(ENDPOINTS.ROOM_PARENT_ADMIN.TODO, {
      networkId,
      task,
      assignedTo,
      ...(dueDate ? { dueDate } : {}),
      ...(eventId ? { eventId } : {}),
      ...(status ? { status } : {}),
    });
    const data = res?.data ?? res;
    return normalizeTodo(data?.data || data);
  },

  /* Partial update — only todoId is required. Send dueDate: null to clear the
     date and eventId: '' to move the task back into General. */
  update: async ({ todoId, ...fields }) => {
    const res = await apiClient.put(ENDPOINTS.ROOM_PARENT_ADMIN.TODO, { todoId, ...fields });
    const data = res?.data ?? res;
    return normalizeTodo(data?.data || data);
  },

  /* Tick a task off, or take your own mark back off with isDone: false. Only an
     assignee or whoever created the task may do either — anyone else gets a 403.
     The reply carries the task's completion after the change, which is what the
     caller should trust: reopening only removes *your* mark, so a task another
     room parent has also ticked stays done. */
  setDone: async ({ todoId, isDone = true }) => {
    const res = await apiClient.patch(ENDPOINTS.ROOM_PARENT_ADMIN.TODO_DONE, { todoId, isDone });
    const data = res?.data ?? res;
    const t = data?.data || data;
    /* only the completion itself — this reply doesn't populate who completed it,
       so leave the row's existing `doneBy` names alone */
    const { done, doneByMe, doneCount } = doneState(t);
    return { done, doneByMe, doneCount, status: t?.status || 'pending' };
  },

  /* Removes the task for everyone on the class. todoId is the task's `todoId`
     (what normalizeTodo puts in `id`). */
  remove: (networkId, todoId) => apiClient.delete(ENDPOINTS.ROOM_PARENT_ADMIN.TODO_BY_ID(networkId, todoId)),
};

export default todosService;
